import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { SendMailModalComponent } from '@app/shared/components/send-mail-modal/send-mail-modal.component';
import { ChangePasswordRequestComponent } from '@app/shared/components/change-password-request/change-password-request.component';
import { LogoComponent } from '@app/shared/components/logo/logo.component';
import { NotFoundComponent } from '@app/shared/components/not-found/not-found.component';
import { MaterialModule } from '@app/modules/material/material.module';
import { SerialTagsComponent } from '@app/shared/components/serial-tags/serial-tags.component';
import { SerialStockComponent } from '@app/shared/components/serial-stock/serial-stock.component';
import { SerialFormComponent } from '@app/shared/components/serial-form/serial-form.component';
import { BarcodeChipsComponent } from '@app/shared/components/barcode-chips/barcode-chips.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TitleComponent } from '@app/shared/components/title/title.component';
import { MatButtonModule } from '@angular/material/button';
import { LogoDarkComponent } from '@app/shared/components/logo-dark/logo-dark.component';
import { RouterModule } from '@angular/router';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpErrorInterceptor } from '@app/interceptors/error/http-error.interceptor';
import { LanguageSelectorComponent } from './components/language-selector/language-selector.component';
import { TranslateModule } from '@ngx-translate/core';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { LongCardComponent } from './components/long-card/long-card.component';
import { MidHeaderComponent } from '@app/modules/counts/mid-header/mid-header.component';
import { DragToSelectModule } from 'ngx-drag-to-select';
import { ClientLogoComponent } from '@app/shared/components/client-logo/client-logo.component';
import { ReportLogoComponent } from './components/report-logo/report-logo.component';
import { ContainerComponent } from './components/container/container.component';
import { ImageCropperComponent } from '@app/shared/components/image-cropper/image-cropper.component';
import { LabelPreviewComponent } from '@app/shared/components/label-preview/label-preview.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { LowstockPieChartComponent } from '@app/shared/components/charts/lowstock-pie-chart/lowstock-pie-chart.component';
import { MasterSorterComponent } from './components/master-sorter/master-sorter.component';
import { CdkScrollableModule, ScrollingModule } from '@angular/cdk/scrolling';
import { ProfileImageComponent } from './profile-image/profile-image.component';
import { NgChartsModule } from 'ng2-charts';
import { ErrorAlertComponent } from '@app/shared/components/error-alert/error-alert.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { ScrollDirective } from '@app/directives/scroll.directive';
import { LightDarkSwitchComponent } from './components/light-dark-switch/light-dark-switch.component';
import { QuaggaScannerComponent } from './components/quagga-scanner/quagga-scanner.component';
import { ItemComponent } from './components/quagga-scanner/item/item.component';
import { TokenInterceptor } from '@app/interceptors/token/token.interceptor';
import { InventorySortTypeInterceptor } from '@app/interceptors/inventory-sort-type/inventory-sort-type.interceptor';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { HtmlSafePipe } from './html-safe.pipe';
import { ConfirmUpdateComponent } from './components/confirm-update/confirm-update.component';
import { ResultsTableComponent } from './components/results-table/results-table.component';
import { TableSkeletonComponent } from './components/skeletons/table-skeleton/table-skeleton.component';
import { ConfirmModelFinalizeComponent } from './components/confirm-model-finalize/confirm-model-finalize.component';
import { VarianceOverrideDialogComponent } from './components/variance-override-dialog/variance-override-dialog.component';
import { StorageOverrideDialogComponent } from './components/storage-override-dialog/storage-override-dialog.component';
import { QuickPickDialogComponent } from './components/quick-pick-dialog/quick-pick-dialog.component';
import { QuickReceiveDialogComponent } from './components/quick-receive-dialog/quick-receive-dialog.component';
import { StandardTableComponent } from './components/tables/standard-table/standard-table.component';
import { RowMenuComponent } from './components/row-menu/row-menu.component';
import { MinimalTableComponent } from './components/tables/minimal-table/minimal-table.component';
import { StackedTableComponent } from './components/tables/stacked-table/stacked-table.component';
import { MessageModalComponent } from './components/message-modal/message-modal.component';
import { ModalExcelReportComponent } from './components/modal-excel-report/modal-excel-report.component';
import { SessionExpiredComponent } from './components/session-expired/session-expired.component';
import { SanitizeStringPipe } from './pipes/sanitize-string.pipe';
import { CategoryTreePipe } from './pipes/category-tree.pipe';
import { PermissionModalComponent } from './components/permission-modal/permission-modal.component';
import { ProductStorageTreeviewComponent } from './components/product-storage-treeview/product-storage-treeview.component';
import { LocationTemplateModalComponent } from './components/location-template-modal/location-template-modal.component';
import { MatTreeModule } from '@angular/material/tree';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ConfirmMessageModalComponent } from './components/confirm-message-modal/confirm-message-modal.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { VideoGridComponent } from './components/video-grid/video-grid.component';
import { HelpVideosDialogComponent } from './components/help-videos-dialog/help-videos-dialog.component';
import { MobileAppPromoComponent } from './components/mobile-app-promo/mobile-app-promo.component';
import { NoticeModalComponent } from '@app/shared/components/notice-modal/notice-modal.component';

@NgModule({
  declarations: [
    ErrorAlertComponent,
    ConfirmModalComponent,
    ConfirmMessageModalComponent,
    NoticeModalComponent,
    SendMailModalComponent,
    MessageModalComponent,
    ModalExcelReportComponent,
    ChangePasswordRequestComponent,
    LogoComponent,
    NotFoundComponent,
    BarcodeChipsComponent,
    SerialTagsComponent,
    SerialStockComponent,
    SerialFormComponent,
    TitleComponent,
    LogoDarkComponent,
    LanguageSelectorComponent,
    NoResultsComponent,
    LongCardComponent,
    MidHeaderComponent,
    ClientLogoComponent,
    ReportLogoComponent,
    ContainerComponent,
    ImageCropperComponent,
    LabelPreviewComponent,
    LowstockPieChartComponent,
    MasterSorterComponent,
    ProfileImageComponent,
    SpinnerComponent,
    ScrollDirective,
    LightDarkSwitchComponent,
    QuaggaScannerComponent,
    ItemComponent,
    HtmlSafePipe,
    ConfirmUpdateComponent,
    ResultsTableComponent,
    TableSkeletonComponent,
    ConfirmModelFinalizeComponent,
    VarianceOverrideDialogComponent,
    StorageOverrideDialogComponent,
    QuickPickDialogComponent,
    QuickReceiveDialogComponent,
    StandardTableComponent,
    RowMenuComponent,
    MinimalTableComponent,
    StackedTableComponent,
    SessionExpiredComponent,
    SanitizeStringPipe,
    CategoryTreePipe,
    PermissionModalComponent,
    ProductStorageTreeviewComponent,
    LocationTemplateModalComponent,
    VideoGridComponent,
    HelpVideosDialogComponent,
    MobileAppPromoComponent
  ],
  imports: [
    MaterialModule,
    FlexLayoutModule,
    CommonModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DragDropModule,
    OverlayModule,
    DragToSelectModule.forRoot(),
    ImageCropperModule,
    CdkScrollableModule,
    ScrollingModule,
    NgChartsModule,
    NgxSkeletonLoaderModule,
    MatGridListModule,
    MatTreeModule,
    MatProgressBarModule,
    RouterModule
  ],
  exports: [
    RouterModule,
    SanitizeStringPipe,
    MaterialModule,
    SpinnerComponent,
    ErrorAlertComponent,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    BarcodeChipsComponent,
    SerialTagsComponent,
    SerialStockComponent,
    SerialFormComponent,
    FlexLayoutModule,
    LogoComponent,
    TitleComponent,
    LogoDarkComponent,
    LanguageSelectorComponent,
    TranslateModule,
    DragDropModule,
    OverlayModule,
    LongCardComponent,
    NoResultsComponent,
    MidHeaderComponent,
    DragToSelectModule,
    ClientLogoComponent,
    ReportLogoComponent,
    ContainerComponent,
    ImageCropperComponent,
    LabelPreviewComponent,
    ImageCropperModule,
    LowstockPieChartComponent,
    MasterSorterComponent,
    CdkScrollableModule,
    ScrollingModule,
    ProfileImageComponent,
    NgChartsModule,
    ScrollDirective,
    LightDarkSwitchComponent,
    QuaggaScannerComponent,
    ItemComponent,
    NgxSkeletonLoaderModule,
    HtmlSafePipe,
    CategoryTreePipe,
    TableSkeletonComponent,
    ConfirmModelFinalizeComponent,
    VarianceOverrideDialogComponent,
    StorageOverrideDialogComponent,
    QuickPickDialogComponent,
    QuickReceiveDialogComponent,
    StandardTableComponent,
    RowMenuComponent,
    MinimalTableComponent,
    ProductStorageTreeviewComponent,
    LocationTemplateModalComponent,
    VideoGridComponent,
    HelpVideosDialogComponent,
    MobileAppPromoComponent,
    MatProgressBarModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: InventorySortTypeInterceptor,
      multi: true
    },
  ]
})
export class SharedModule { }


